<template>
  <div class="licenses">
    <n-card>
      <template #header>
        <div class="card-header">
          <span>All Licenses</span>
          <n-space>
            <n-select
              v-model:value="filters.status"
              :options="statusFilterOptions"
              placeholder="Status"
              clearable
              style="width: 150px"
            />
            <n-select
              v-model:value="filters.module"
              :options="moduleFilterOptions"
              placeholder="Module"
              clearable
              style="width: 150px"
            />
            <n-checkbox v-model:checked="filters.expiring">
              Expiring Soon
            </n-checkbox>
          </n-space>
        </div>
      </template>

      <n-data-table
        :columns="columns"
        :data="licenses"
        :loading="loading"
        :row-key="(row) => row.id"
        :default-sort="{ columnKey: 'expiry_date', order: 'ascend' }"
      />
    </n-card>

    <!-- Edit License Modal -->
    <n-modal v-model:show="showEditModal" preset="dialog" title="Edit License">
      <n-form :model="editForm" label-placement="left" label-width="120">
        <n-form-item label="Status">
          <n-select
            v-model:value="editForm.status"
            :options="statusOptions"
            placeholder="Select status"
          />
        </n-form-item>
        <n-form-item label="Expiry Date">
          <n-date-picker v-model:value="editForm.expiry_date" type="date" />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showEditModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleUpdateLicense">
          Save Changes
        </n-button>
      </template>
    </n-modal>

    <!-- Renew License Modal -->
    <n-modal v-model:show="showRenewModal" preset="dialog" title="Renew License">
      <n-form :model="renewForm" label-placement="left" label-width="120">
        <n-form-item label="New Expiry Date">
          <n-date-picker v-model:value="renewForm.new_expiry_date" type="date" />
        </n-form-item>
        <n-form-item label="Amount Paid">
          <n-input-number v-model:value="renewForm.amount_paid" :min="0" />
        </n-form-item>
        <n-form-item label="Payment Date">
          <n-date-picker v-model:value="renewForm.payment_date" type="date" />
        </n-form-item>
        <n-form-item label="Payment Method">
          <n-select
            v-model:value="renewForm.payment_method"
            :options="paymentMethodOptions"
            placeholder="Select method"
          />
        </n-form-item>
      </n-form>
      <template #action>
        <n-button @click="showRenewModal = false">Cancel</n-button>
        <n-button type="primary" :loading="saving" @click="handleRenewLicense">
          Renew
        </n-button>
      </template>
    </n-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, h } from 'vue'
import {
  NCard,
  NDataTable,
  NSelect,
  NCheckbox,
  NSpace,
  NTag,
  NButton,
  NModal,
  NForm,
  NFormItem,
  NDatePicker,
  NInputNumber,
} from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAdmin } from '../composables/useAdmin'
import dayjs from 'dayjs'

const toast = useToast()
const { listLicenses, updateLicense, renewLicense, listAvailableModules, loading } = useAdmin()

const licenses = ref([])
const showEditModal = ref(false)
const showRenewModal = ref(false)
const saving = ref(false)
const selectedLicense = ref(null)

const filters = ref({
  status: null,
  module: null,
  expiring: false,
})

const editForm = ref({
  status: null,
  expiry_date: null,
})

const renewForm = ref({
  new_expiry_date: null,
  amount_paid: 0,
  payment_date: Date.now(),
  payment_method: 'cash',
})

const statusFilterOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
  { label: 'Trial', value: 'trial' },
  { label: 'Suspended', value: 'suspended' },
]

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
]

// Available modules loaded from API
const moduleFilterOptions = ref([])

const paymentMethodOptions = [
  { label: 'Cash', value: 'cash' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'UPI', value: 'upi' },
  { label: 'Online', value: 'online' },
]

const columns = [
  { title: 'ID', key: 'id', width: 60, sorter: 'default' },
  { title: 'Business', key: 'business_name', sorter: 'default' },
  { title: 'Module', key: 'module_name', sorter: 'default' },
  {
    title: 'Start Date',
    key: 'start_date',
    sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
    render: (row) => dayjs(row.start_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Expiry Date',
    key: 'expiry_date',
    sorter: (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date),
    render: (row) => dayjs(row.expiry_date).format('MMM DD, YYYY'),
  },
  {
    title: 'Days Left',
    key: 'days_remaining',
    sorter: (a, b) => a.days_remaining - b.days_remaining,
    render: (row) => {
      if (row.is_expired) {
        return h(NTag, { type: 'error', size: 'small' }, () => 'Expired')
      }
      if (row.days_remaining <= 7) {
        return h(NTag, { type: 'warning', size: 'small' }, () => `${row.days_remaining} days`)
      }
      if (row.days_remaining <= 30) {
        return h(NTag, { type: 'warning', size: 'small' }, () => `${row.days_remaining} days`)
      }
      return h(NTag, { type: 'success', size: 'small' }, () => `${row.days_remaining} days`)
    },
  },
  {
    title: 'Status',
    key: 'status',
    sorter: 'default',
    render: (row) => {
      const types = { active: 'success', expired: 'error', trial: 'info', suspended: 'warning' }
      return h(NTag, { type: types[row.status] || 'default', size: 'small' }, () => row.status)
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (row) => {
      return h(NSpace, { size: 'small' }, () => [
        h(
          NButton,
          {
            size: 'small',
            onClick: () => openEditModal(row),
          },
          () => 'Edit'
        ),
        h(
          NButton,
          {
            size: 'small',
            type: 'primary',
            onClick: () => openRenewModal(row),
          },
          () => 'Renew'
        ),
      ])
    },
  },
]

const openEditModal = (license) => {
  selectedLicense.value = license
  editForm.value = {
    status: license.status,
    expiry_date: dayjs(license.expiry_date).valueOf(),
  }
  showEditModal.value = true
}

const openRenewModal = (license) => {
  selectedLicense.value = license
  renewForm.value = {
    new_expiry_date: dayjs(license.expiry_date).add(1, 'year').valueOf(),
    amount_paid: 0,
    payment_date: Date.now(),
    payment_method: 'cash',
  }
  showRenewModal.value = true
}

const handleUpdateLicense = async () => {
  saving.value = true
  try {
    await updateLicense(selectedLicense.value.id, {
      status: editForm.value.status,
      expiry_date: dayjs(editForm.value.expiry_date).format('YYYY-MM-DD'),
    })
    toast.success('License updated successfully')
    showEditModal.value = false
    loadLicenses()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const handleRenewLicense = async () => {
  saving.value = true
  try {
    await renewLicense(selectedLicense.value.id, selectedLicense.value.business_id, {
      new_expiry_date: dayjs(renewForm.value.new_expiry_date).format('YYYY-MM-DD'),
      amount_paid: renewForm.value.amount_paid,
      payment_date: dayjs(renewForm.value.payment_date).format('YYYY-MM-DD'),
      payment_method: renewForm.value.payment_method,
    })
    toast.success('License renewed successfully')
    showRenewModal.value = false
    loadLicenses()
  } catch (error) {
    toast.error(error.message)
  } finally {
    saving.value = false
  }
}

const loadLicenses = async () => {
  try {
    const params = {}
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.module) params.module = filters.value.module
    if (filters.value.expiring) params.expiring = true

    const response = await listLicenses(params)
    licenses.value = response.licenses || []
  } catch (error) {
    console.error('Failed to load licenses:', error)
  }
}

watch(filters, loadLicenses, { deep: true })

// Load available modules from registry for filter dropdown
const loadModulesForFilter = async () => {
  try {
    const response = await listAvailableModules()
    moduleFilterOptions.value = (response.modules || []).map(m => ({
      label: m.display_name,
      value: m.name,
    }))
  } catch (error) {
    console.error('Failed to load available modules:', error)
  }
}

onMounted(async () => {
  await loadModulesForFilter()
  await loadLicenses()
})
</script>

<style scoped>
.licenses {
  max-width: 1400px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
